import { useEffect } from "react";
import { useLocation } from "react-router-dom";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  type?: "website" | "article" | "service";
  schema?: object;
  image?: string;
  noindex?: boolean;
}

const BASE_URL = "https://onyxprofessionalplumbing.com";
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;
const SITE_NAME = "Onyx Professional Plumbing";
const PHONE = "(251) 504-4452";

export const SEO = ({ 
  title, 
  description, 
  canonical, 
  type = "website", 
  schema,
  image = DEFAULT_IMAGE,
  noindex = false
}: SEOProps) => {
  const location = useLocation();
  
  useEffect(() => {
    // Generate canonical URL - ensure no trailing slash except for root
    const path = location.pathname === "/" ? "" : location.pathname.replace(/\/$/, "");
    const fullCanonical = canonical || `${BASE_URL}${path}`;
    
    // Update document title
    document.title = title;

    // Helper to update or create meta tag
    const setMetaTag = (selector: string, content: string, isProperty = false) => {
      let meta = document.querySelector(selector) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement("meta");
        if (isProperty) {
          const prop = selector.match(/property="([^"]+)"/)?.[1];
          if (prop) meta.setAttribute("property", prop);
        } else {
          const name = selector.match(/name="([^"]+)"/)?.[1];
          if (name) meta.setAttribute("name", name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", content);
    };

    // Basic meta tags
    setMetaTag('meta[name="description"]', description);
    setMetaTag('meta[name="robots"]', noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    
    // Open Graph tags
    setMetaTag('meta[property="og:title"]', title, true);
    setMetaTag('meta[property="og:description"]', description, true);
    setMetaTag('meta[property="og:type"]', type, true);
    setMetaTag('meta[property="og:url"]', fullCanonical, true);
    setMetaTag('meta[property="og:site_name"]', SITE_NAME, true);
    setMetaTag('meta[property="og:locale"]', "en_US", true);
    setMetaTag('meta[property="og:image"]', image, true);
    
    // Twitter Card tags
    setMetaTag('meta[name="twitter:card"]', "summary_large_image");
    setMetaTag('meta[name="twitter:title"]', title);
    setMetaTag('meta[name="twitter:description"]', description);
    setMetaTag('meta[name="twitter:image"]', image);

    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute("href", fullCanonical);

    // Add page-specific schema if provided
    if (schema) {
      const existingPageSchema = document.getElementById("page-schema");
      if (existingPageSchema) {
        existingPageSchema.remove();
      }

      const schemaScript = document.createElement("script");
      schemaScript.id = "page-schema";
      schemaScript.type = "application/ld+json";
      schemaScript.text = JSON.stringify(schema);
      document.head.appendChild(schemaScript);

      return () => {
        schemaScript.remove();
      };
    }
  }, [title, description, canonical, type, schema, image, noindex, location.pathname]);

  return null;
};

// Pre-built schemas for common page types
export const createServiceSchema = (service: {
  name: string;
  description: string;
  url: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": service.name,
  "provider": {
    "@type": "Plumber",
    "name": SITE_NAME,
    "telephone": "+1-251-504-4452",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "6642 AL-59 Unit D-3",
      "addressLocality": "Gulf Shores",
      "addressRegion": "AL",
      "postalCode": "36542"
    }
  },
  "areaServed": {
    "@type": "State",
    "name": "Alabama",
    "containsPlace": [
      { "@type": "City", "name": "Gulf Shores" },
      { "@type": "City", "name": "Orange Beach" },
      { "@type": "City", "name": "Foley" },
      { "@type": "City", "name": "Fairhope" },
      { "@type": "City", "name": "Daphne" },
      { "@type": "City", "name": "Spanish Fort" },
      { "@type": "City", "name": "Robertsdale" },
      { "@type": "City", "name": "Bay Minette" },
      { "@type": "City", "name": "Loxley" },
      { "@type": "City", "name": "Summerdale" },
      { "@type": "City", "name": "Elberta" },
      { "@type": "City", "name": "Perdido Beach" }
    ]
  },
  "description": service.description,
  "url": service.url
});

export const createBreadcrumbSchema = (items: { name: string; url: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

// Create local business schema for city pages
export const createCitySchema = (city: string, description: string, zipCodes: string[]) => ({
  "@context": "https://schema.org",
  "@type": "Plumber",
  "@id": `https://onyxprofessionalplumbing.com/${city.toLowerCase().replace(/\s+/g, '-')}-plumber#business`,
  "name": `Onyx Professional Plumbing - ${city} Plumber`,
  "description": description,
  "url": `https://onyxprofessionalplumbing.com/${city.toLowerCase().replace(/\s+/g, '-')}-plumber`,
  "telephone": "+1-251-504-4452",
  "email": "onyxproplumbing@gmail.com",
  "priceRange": "$$",
  "image": DEFAULT_IMAGE,
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "6642 AL-59 Unit D-3",
    "addressLocality": "Gulf Shores",
    "addressRegion": "AL",
    "postalCode": "36542",
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "30.2460",
    "longitude": "-87.7008"
  },
  "areaServed": {
    "@type": "City",
    "name": city,
    "containedInPlace": {
      "@type": "AdministrativeArea",
      "name": "Baldwin County, Alabama"
    }
  },
  "serviceArea": zipCodes.map(zip => ({
    "@type": "PostalAddress",
    "postalCode": zip,
    "addressCountry": "US"
  })),
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "07:00",
      "closes": "18:00"
    },
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Saturday", "Sunday"],
      "opens": "08:00",
      "closes": "16:00"
    }
  ],
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "5.0",
    "bestRating": "5",
    "worstRating": "1",
    "ratingCount": "50"
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": `Plumbing Services in ${city}`,
    "itemListElement": [
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Emergency Plumbing", "description": `24/7 emergency plumbing repairs in ${city}` }},
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Water Heater Services", "description": `Water heater repair and installation in ${city}` }},
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Drain Cleaning", "description": `Professional drain cleaning in ${city}` }},
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Residential Plumbing", "description": `Home plumbing services in ${city}` }},
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Commercial Plumbing", "description": `Business plumbing in ${city}` }}
    ]
  }
});
